import React, { useState } from 'react';
import { MdArrowOutward, MdLocationOn } from 'react-icons/md';
import {
  getCollegeBanner,
  getCollegeLocation,
  getCollegeLogo,
  getCollegeName,
  getCollegeShortName
} from '../utils/collegeListing';

const CollegeCard = ({ college, onViewProfile, showViewProfile = true, className = '', compact = false }) => {
  const [logoSrc, setLogoSrc] = useState(getCollegeLogo(college));
  const [bannerSrc, setBannerSrc] = useState(getCollegeBanner(college));
  const name = getCollegeName(college);
  const shortName = getCollegeShortName(college);
  const location = getCollegeLocation(college);
  if (compact) {
    return (
      <article className="group overflow-hidden rounded-2xl border border-white/70 bg-white/95 shadow-[0_16px_36px_-24px_rgba(15,23,42,0.3)]">
        <div className="flex gap-3 p-3">
          <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-xl bg-slate-100">
            <img
              src={bannerSrc}
              alt={`${name} campus`}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
              onError={() => setBannerSrc('/default-images/campus/default_campus.png')}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/25 via-transparent to-transparent" />
          </div>

          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex items-start gap-2">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-white bg-white shadow-sm">
                <img
                  src={logoSrc}
                  alt={`${name} logo`}
                  className="h-full w-full object-contain p-1.5"
                  loading="lazy"
                  onError={() => setLogoSrc('/default-images/Logo/default_logo.png')}
                />
              </div>

              <div className="min-w-0 flex-1">
                <h3 className="line-clamp-3 text-[15px] font-semibold leading-5 text-slate-900 break-words">
                  {name}
                </h3>
                <p className="mt-1 line-clamp-1 text-[10px] font-medium uppercase tracking-[0.14em] text-[#9a51a0]">
                  {shortName || 'College Profile'}
                </p>
              </div>
            </div>

            <div className="mt-2 flex items-center gap-2 text-xs leading-5 text-slate-600">
              <span className="rounded-full bg-orange-50 p-1.5 text-[#f36f21] shrink-0">
                <MdLocationOn className="h-3.5 w-3.5" />
              </span>
              <span className="line-clamp-2 break-words">{location}</span>
            </div>

            {showViewProfile ? (
              <button
                type="button"
                onClick={() => onViewProfile(college)}
                className="mt-auto inline-flex items-center gap-1.5 pt-3 text-left text-xs font-semibold text-[#9a51a0]"
              >
                <span>View Full Profile</span>
                <MdArrowOutward className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className={`group overflow-hidden rounded-2xl border border-white/70 bg-white/95 shadow-[0_18px_45px_-24px_rgba(15,23,42,0.32)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_26px_60px_-28px_rgba(154,81,160,0.42)] ${className}`}>
      <div className="relative h-32 overflow-hidden bg-slate-100">
        <img
          src={bannerSrc}
          alt={`${name} campus`}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
          onError={() => setBannerSrc('/default-images/campus/default_campus.png')}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/35 via-slate-900/10 to-transparent" />
      </div>

      <div className="p-4">
        <div className="mb-3 flex items-start gap-3">
          <div className="relative -mt-9 z-10 flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl border-4 border-white bg-white shadow-lg">
            <img
              src={logoSrc}
              alt={`${name} logo`}
              className="h-full w-full object-contain p-2"
              loading="lazy"
              onError={() => setLogoSrc('/default-images/Logo/default_logo.png')}
            />
          </div>

          <div className="min-w-0 flex-1 pt-2">
            <h3 className="line-clamp-2 text-sm font-semibold leading-5 text-slate-900 sm:text-base">
              {name}
            </h3>
            <p className="mt-1 text-xs font-medium uppercase tracking-[0.18em] text-[#9a51a0]">
              {shortName || 'College Profile'}
            </p>
          </div>
        </div>

        <div className="mb-4 flex items-center gap-2 text-sm text-slate-600">
          <span className="rounded-full bg-orange-50 p-2 text-[#f36f21]">
            <MdLocationOn className="h-4 w-4" />
          </span>
          <span className="line-clamp-1">{location}</span>
        </div>

        {showViewProfile && (
          <button
            type="button"
            onClick={() => onViewProfile(college)}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#9a51a0] to-[#f36f21] px-4 py-3 text-sm font-semibold text-white shadow-md transition-all duration-300 hover:shadow-lg"
          >
            <span>View Full Profile</span>
            <MdArrowOutward className="h-4 w-4" />
          </button>
        )}
      </div>
    </article>
  );
};

export default CollegeCard;
